import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, TrendingUp, Users, Settings, UserPlus, Send, FileText, Activity, CheckCircle2, AlertTriangle, XCircle, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TrafficBadge } from "@/components/TrafficBadge";
import { KPISparkline } from "@/components/MemoAI/KPISparkline";
import { AppShell } from "@/components/layout/AppShell";
import { SkeletonCompanyDetail } from "@/components/ui/skeleton-card";
import { useCompany, useKPIs, useReports, useActivityFeed, usePendingKPIs } from "@/hooks/useCompanyData";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: company, isLoading: companyLoading, error: companyError } = useCompany(id);
  const { data: kpis = [] } = useKPIs(id);
  const { data: reports = [] } = useReports(id);
  const { data: activity = [] } = useActivityFeed(id);
  const { data: pendingData } = usePendingKPIs(id);

  // KPI values (loaded alongside KPIs)
  const [kpiValues, setKPIValues] = useState<Record<string, { value: number; week_start_date: string }[]>>({});
  const [valuesLoading, setValuesLoading] = useState(false);

  useEffect(() => {
    if (kpis.length > 0) loadKPIValues();
  }, [kpis]);

  const loadKPIValues = async () => {
    setValuesLoading(true);
    try {
      const kpiIds = kpis.map((k) => k.id);
      const { data, error } = await supabase
        .from("kpi_values")
        .select("kpi_id, value, week_start_date")
        .in("kpi_id", kpiIds)
        .order("week_start_date", { ascending: true });
      if (error) throw error;

      const grouped: Record<string, { value: number; week_start_date: string }[]> = {};
      for (const v of data || []) {
        if (!grouped[v.kpi_id]) grouped[v.kpi_id] = [];
        grouped[v.kpi_id].push(v);
      }
      setKPIValues(grouped);
    } catch {
      /* silent - values are supplementary */
    } finally {
      setValuesLoading(false);
    }
  };

  useEffect(() => {
    if (companyError) {
      toast.error("Failed to load company");
      navigate("/app");
    }
  }, [companyError]);

  const getKPIStatus = (kpi: typeof kpis[0]): "green" | "amber" | "red" | "neutral" => {
    const values = kpiValues[kpi.id];
    if (!values || values.length === 0) return "neutral";
    const latestValue = values[values.length - 1]?.value;
    if (latestValue === undefined) return "neutral";
    if (kpi.min_band && latestValue < kpi.min_band) return "red";
    if (kpi.max_band && latestValue > kpi.max_band) return "red";
    if (values.length >= 2) {
      const previousValue = values[values.length - 2]?.value;
      if (previousValue) {
        const change = Math.abs((latestValue - previousValue) / previousValue);
        if (change > 0.2) return "amber";
      }
    }
    return "green";
  };

  // Summary card stats
  const statusCounts = kpis.reduce(
    (acc, kpi) => {
      const s = getKPIStatus(kpi);
      if (s === "green") acc.green++;
      else if (s === "amber") acc.amber++;
      else if (s === "red") acc.red++;
      return acc;
    },
    { green: 0, amber: 0, red: 0 }
  );
  const completionRate = kpis.length > 0 && pendingData
    ? Math.round(((kpis.length - pendingData.pending) / kpis.length) * 100)
    : 0;

  const loading = companyLoading;

  if (loading) {
    return (
      <AppShell title="Loading..." showBack backTo="/app">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-6xl">
          <SkeletonCompanyDetail />
        </div>
      </AppShell>
    );
  }

  if (!company) return null;

  return (
    <AppShell title={company.name} subtitle={`${kpis.length} KPIs configured`} showBack backTo="/app" companyId={id} companyName={company.name}>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Completion</p>
                <p className="text-2xl font-bold tabular-nums">{completionRate}%</p>
                <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${completionRate}%` }} />
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-l-4 border-l-success">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> On Track</p>
                <p className="text-2xl font-bold tabular-nums text-success">{statusCounts.green}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-l-4 border-l-warning">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Watch</p>
                <p className="text-2xl font-bold tabular-nums text-warning">{statusCounts.amber}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-l-4 border-l-danger">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><XCircle className="h-3 w-3" /> Attention</p>
                <p className="text-2xl font-bold tabular-nums text-danger">{statusCounts.red}</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="kpis" className="w-full">
            <TabsList className="w-full sm:w-auto grid grid-cols-5 sm:flex rounded-2xl bg-muted/50 p-1">
              <TabsTrigger value="kpis" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <TrendingUp className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">KPIs</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <FileText className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Reports</span>
              </TabsTrigger>
              <TabsTrigger value="team" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <UserPlus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Team</span>
              </TabsTrigger>
              <TabsTrigger value="activity" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Activity className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Activity</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Settings className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="kpis" className="space-y-6 mt-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-muted-foreground">Track your key metrics and generate weekly reports</p>
                <div className="flex gap-2">
                  {kpis.length > 0 && (
                    <Button onClick={() => navigate(`/app/companies/${id}/submit`)} className="flex-1 sm:flex-none rounded-2xl bg-gradient-accent shadow-md">
                      <Send className="h-4 w-4 mr-2" />Quick Submit
                    </Button>
                  )}
                  <Button onClick={() => navigate(`/app/companies/${id}/kpis/new`)} variant="outline" className="flex-1 sm:flex-none rounded-2xl">
                    <Plus className="h-4 w-4 mr-2" />Add KPI
                  </Button>
                </div>
              </div>

              {kpis.length === 0 ? (
                <Card className="rounded-3xl border-dashed border-2 bg-card/50">
                  <CardContent className="py-16 text-center">
                    <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                      <TrendingUp className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">No KPIs yet</h3>
                    <p className="text-muted-foreground mb-8 max-w-sm mx-auto">Add 3-9 KPIs to start tracking your metrics</p>
                    <Button onClick={() => navigate(`/app/companies/${id}/kpis/new`)} className="rounded-2xl shadow-md bg-gradient-premium">
                      <Plus className="h-4 w-4 mr-2" />Add first KPI
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {kpis.map((kpi, index) => {
                    const status = getKPIStatus(kpi);
                    const values = kpiValues[kpi.id] || [];
                    const latestValue = values[values.length - 1]?.value;
                    return (
                      <Card
                        key={kpi.id}
                        className={cn("cursor-pointer rounded-2xl border-border/50 transition-all duration-300", "hover:shadow-lg hover:border-primary/20 hover:bg-card-hover", "active:scale-[0.99]")}
                        onClick={() => navigate(`/app/companies/${id}/kpis/${kpi.id}`)}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <CardHeader className="p-5">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <TrafficBadge status={status} />
                              <div className="min-w-0 flex-1">
                                <CardTitle className="text-base font-semibold truncate">{kpi.name}</CardTitle>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {values.length > 1 && (
                                <div className="hidden sm:block w-24 h-8">
                                  <KPISparkline data={values.map((v) => v.value)} color={status === "red" ? "red" : status === "amber" ? "amber" : status === "green" ? "green" : "default"} />
                                </div>
                              )}
                              <div className="text-right">
                                <p className="text-lg font-semibold tabular-nums">
                                  {latestValue !== undefined ? `${latestValue}${kpi.unit}` : "—"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {kpi.min_band && kpi.max_band ? `Target: ${kpi.min_band}–${kpi.max_band}${kpi.unit}` : "No target set"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="reports" className="mt-8 space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-lg font-semibold">Weekly Reports</h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={async () => {
                      toast.info("Exporting data...");
                      try {
                        const { data, error } = await supabase.functions.invoke("export-data", {
                          body: { company_id: id, format: "csv" },
                        });
                        if (error) throw error;
                        // Download CSV
                        const blob = new Blob([typeof data === "string" ? data : JSON.stringify(data)], { type: "text/csv" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${company?.name || "export"}_kpi_data.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success("Data exported!");
                      } catch {
                        toast.error("Failed to export data");
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={async () => {
                      toast.info("Generating report...");
                      try {
                        const { error } = await supabase.functions.invoke("generate-report", {
                          body: { companyId: id },
                        });
                        if (error) throw error;
                        qc.invalidateQueries({ queryKey: ["reports", id] });
                        toast.success("Report generated!");
                      } catch {
                        toast.error("Failed to generate report");
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />Generate Report
                  </Button>
                </div>
              </div>
              {reports.length === 0 ? (
                <Card className="rounded-2xl">
                  <CardContent className="py-12 text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No reports generated yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <Card key={report.id} className="rounded-2xl">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Week of {new Date(report.week_start_date).toLocaleDateString()}</p>
                          <p className="text-xs text-muted-foreground">{new Date(report.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                          {report.pdf_url && (
                            <Button size="sm" variant="outline" className="rounded-xl" asChild>
                              <a href={report.pdf_url} target="_blank" rel="noopener noreferrer">PDF</a>
                            </Button>
                          )}
                          {report.csv_url && (
                            <Button size="sm" variant="outline" className="rounded-xl" asChild>
                              <a href={report.csv_url} target="_blank" rel="noopener noreferrer">CSV</a>
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="team" className="mt-8">
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                    <UserPlus className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Manage your team</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Invite team members to collaborate on KPIs</p>
                  <Button onClick={() => navigate(`/app/companies/${id}/team`)} className="rounded-2xl">
                    <UserPlus className="h-4 w-4 mr-2" />Manage team
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="mt-8 space-y-3">
              {activity.length === 0 ? (
                <Card className="rounded-2xl">
                  <CardContent className="py-12 text-center">
                    <Activity className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                  </CardContent>
                </Card>
              ) : (
                activity.map((log) => (
                  <Card key={log.id} className="rounded-2xl">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium capitalize">{log.action} {log.entity}</p>
                        <p className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="settings" className="mt-8">
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                    <Settings className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Company settings</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Configure company details and reporting schedule</p>
                  <Button onClick={() => navigate(`/app/companies/${id}/settings`)} className="rounded-2xl">Edit settings</Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppShell>
  );
};

export default CompanyDetail;
